import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// === Designer-Redesign (redesign emergency/) ===
// Layout-Quelle: redesign emergency/code.html + DESIGN.md.
// Texte über translations.emergency.v2.* (DE/EN/ES/RU).
// Tokens: ds-* aus tailwind.config (siehe DESIGN.md).

const TEL = '603319648';
const TEL_DISPLAY = '603 31 96 48';
const WHATSAPP_URL = 'https://wa.me/34603319648';
const MAPS_URL = 'https://maps.google.com/?q=Avenida+de+Jaime+III+3+Palma+de+Mallorca';

// Material Symbols Outlined wird normalerweise nach Cookie-Consent geladen
// (CookieConsent.tsx::loadGoogleFonts). Für die Notfall-Seite laden wir die
// Font hier zusätzlich idempotent — die Page muss auch ohne Consent mit
// Icons funktionieren (funktionale Notwendigkeit, kein Tracking).
const ensureMaterialSymbols = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('material-symbols-outlined-link')) return;
  const link = document.createElement('link');
  link.id = 'material-symbols-outlined-link';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap';
  document.head.appendChild(link);
};

interface MIconProps {
  name: string;
  className?: string;
  filled?: boolean;
}
const MIcon: React.FC<MIconProps> = ({ name, className = '', filled }) => (
  <span
    aria-hidden="true"
    className={`material-symbols-outlined ${className}`}
    style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
  >
    {name}
  </span>
);

const EmergencyPage: React.FC = () => {
  const { translations, language } = useLanguage();
  const v2 = translations.emergency?.v2;

  useEffect(() => {
    ensureMaterialSymbols();
  }, []);

  useEffect(() => {
    let titleText = 'Zahnärztlicher Notdienst 24/7 — SeaDent Mallorca';
    let descriptionText =
      'Zahnärztlicher Notdienst rund um die Uhr in Palma de Mallorca. Bei Zahnschmerzen und Notfällen sofort für Sie da — Tel: 603 31 96 48';
    if (language === 'en') {
      titleText = 'Dental Emergency Service 24/7 — SeaDent Mallorca';
      descriptionText =
        'Round-the-clock dental emergency service in Palma de Mallorca. Immediate help for dental pain and emergencies — Call: 603 31 96 48';
    } else if (language === 'es') {
      titleText = 'Servicio de Urgencias Dentales 24/7 — SeaDent Mallorca';
      descriptionText =
        'Servicio de urgencias dentales las 24 horas en Palma de Mallorca. Ayuda inmediata para dolor dental y emergencias — Llame: 603 31 96 48';
    } else if (language === 'ru') {
      titleText = 'Экстренная стоматологическая помощь 24/7 — SeaDent Mallorca';
      descriptionText =
        'Круглосуточная экстренная стоматологическая помощь в Пальма-де-Майорка. Немедленная помощь при зубной боли и неотложных случаях — Звоните: 603 31 96 48';
    }
    document.title = titleText;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionText);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descriptionText;
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, [language]);

  if (!v2) {
    // Fallback during HMR if translation chunk did not include v2 yet.
    return <div className="pt-24 text-center text-ds-on-surface-variant">…</div>;
  }

  return (
    <main className="bg-ds-surface text-ds-on-background font-ds-body">
      {/* === Hero === */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-ds-primary pt-20 lg:pt-28">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-ds-primary via-ds-primary/80 to-transparent z-10" />
          <img
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            src="/images/SeaDent Treatment.jpg"
          />
        </div>
        <div className="relative z-20 px-gutter w-full py-section-padding-mobile md:py-section-padding-desktop">
          <div className="max-w-container-max mx-auto">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-ds-emergency-red/10 border border-ds-emergency-red/30 text-ds-emergency-red px-4 py-1.5 rounded-full mb-6 animate-ds-emergency-glow">
                <MIcon name="emergency_home" className="text-[18px]" />
                <span className="text-[14px] font-semibold tracking-wider">{v2.heroBadge}</span>
              </div>
              <h1 className="font-ds-display text-[40px] md:text-[48px] leading-tight text-ds-clinical-white mb-6">
                {translations.emergency.title}
                <br />
                <span className="text-ds-secondary-fixed-dim">{v2.heroAccent}</span>
              </h1>
              <p className="font-ds-body text-[18px] leading-7 text-ds-clinical-white/80 mb-10 max-w-lg">
                {v2.heroIntro}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`tel:${TEL}`}
                  className="flex items-center justify-center gap-3 bg-ds-emergency-red text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[18px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
                >
                  <MIcon name="call" />
                  {v2.callBtn}
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-ds-success-green text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[18px] hover:bg-opacity-90 active:scale-[0.98] transition-all shadow-lg"
                >
                  <MIcon name="chat" />
                  {v2.whatsappBtn}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === Stats === */}
      <section className="py-12 bg-ds-clinical-white border-b border-ds-outline-variant/30">
        <div className="max-w-container-max mx-auto px-gutter grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">24/7</p>
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">{v2.stats.availability}</p>
          </div>
          <div className="text-center border-l border-ds-outline-variant/30">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">365</p>
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">{v2.stats.daysAYear}</p>
          </div>
          <div className="text-center border-l border-ds-outline-variant/30">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">&lt;30m</p>
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">{v2.stats.responseTime}</p>
          </div>
          <div className="text-center border-l border-ds-outline-variant/30">
            <p className="text-ds-primary font-bold text-[32px] leading-10 font-ds-display">5.0</p>
            <p className="text-ds-on-surface-variant text-[14px] font-semibold tracking-wider uppercase">{v2.stats.rating}</p>
          </div>
        </div>
      </section>

      {/* === Common Emergencies === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter">
        <div className="max-w-container-max mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-12">
            <div className="max-w-xl">
              <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-4">{v2.common.title}</h2>
              <p className="text-ds-on-surface-variant text-[18px] leading-7">{v2.common.intro}</p>
            </div>
            <div className="hidden md:block">
              <span className="text-ds-outline text-[12px] uppercase tracking-widest font-medium">{v2.common.eyebrow}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {v2.common.cards.map((card: any, i: number) => {
              const iconNames = ['medical_services', 'medication_liquid', 'healing'];
              return (
                <div
                  key={i}
                  className="bg-ds-clinical-white p-8 rounded-2xl shadow-ds-card border border-ds-outline-variant/20 hover:border-ds-secondary transition-colors"
                >
                  <div className="w-14 h-14 bg-ds-secondary-fixed flex items-center justify-center rounded-xl mb-6 text-ds-primary">
                    <MIcon name={iconNames[i] || 'medical_services'} className="text-[32px]" />
                  </div>
                  <h3 className="font-ds-body text-[24px] leading-8 font-semibold text-ds-primary mb-4">{card.title}</h3>
                  <p className="text-ds-on-surface-variant mb-6 text-[16px] leading-6">{card.description}</p>
                  <ul className="space-y-3">
                    {card.checks?.map((check: string, j: number) => (
                      <li key={j} className="flex items-center gap-2 text-[14px] font-semibold text-ds-on-surface">
                        <MIcon name="check_circle" className="text-ds-success-green" filled />
                        {check}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === Process === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop bg-ds-primary text-ds-clinical-white overflow-hidden">
        <div className="max-w-container-max mx-auto px-gutter grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-ds-display text-[28px] md:text-[32px] leading-tight mb-12">{v2.process.title}</h2>
            <div className="space-y-12">
              {v2.process.steps.map((step: any, i: number) => {
                const isFirst = i === 0;
                const isLast = i === v2.process.steps.length - 1;
                return (
                  <div key={i} className="flex gap-6 relative">
                    {!isLast && (
                      <div className="absolute left-6 top-16 bottom-[-3rem] w-[2px] bg-ds-secondary-fixed/20 hidden md:block" />
                    )}
                    <div
                      className={`flex-shrink-0 w-12 h-12 ${
                        isFirst ? 'bg-ds-emergency-red' : 'bg-ds-secondary'
                      } rounded-full flex items-center justify-center font-bold text-xl z-10`}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <h4 className="font-ds-body text-[24px] leading-8 font-semibold mb-2">{step.title}</h4>
                      <p className="text-ds-clinical-white/70 text-[16px] leading-6">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-ds-secondary-fixed/10 rounded-3xl blur-2xl" />
            <img
              alt=""
              aria-hidden="true"
              className="relative rounded-3xl shadow-2xl w-full aspect-[4/5] object-cover"
              src="/images/SeaDent Approach Hands Tooth.jpg"
            />
          </div>
        </div>
      </section>

      {/* === Location === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop px-gutter">
        <div className="max-w-container-max mx-auto bg-ds-clinical-white rounded-3xl shadow-ds-card-lg overflow-hidden flex flex-col md:flex-row">
          <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
            <span className="text-ds-secondary text-[14px] font-semibold uppercase tracking-widest mb-4">{v2.location.eyebrow}</span>
            <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-6">{v2.location.title}</h2>
            <p className="text-ds-on-surface-variant text-[18px] leading-7 mb-8">{v2.location.intro}</p>
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <MIcon name="location_on" className="text-ds-secondary" />
                <div>
                  <p className="font-bold text-ds-primary">{v2.location.addressLine1}</p>
                  <p className="text-ds-on-surface-variant">{v2.location.addressLine2}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MIcon name="schedule" className="text-ds-secondary" />
                <div>
                  <p className="font-bold text-ds-primary">{v2.location.hoursLabel}</p>
                  <p className="text-ds-on-surface-variant">{v2.location.emergencyHours}</p>
                </div>
              </div>
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-ds-primary text-ds-clinical-white px-8 py-4 rounded-xl font-bold text-[14px] tracking-wider uppercase hover:bg-ds-secondary active:scale-[0.98] transition-all self-start"
            >
              <MIcon name="directions" />
              {v2.location.directions}
            </a>
          </div>
          <div className="md:w-1/2 h-[400px] md:h-auto bg-ds-surface-container">
            <iframe
              title="SeaDent Mallorca Map"
              src="https://www.google.com/maps?q=Avenida%20de%20Jaime%20III%203%20Palma%20de%20Mallorca&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* === Final CTA === */}
      <section className="py-section-padding-mobile md:py-section-padding-desktop bg-ds-surface-container-highest">
        <div className="max-w-3xl mx-auto px-gutter text-center">
          <h2 className="font-ds-display text-[32px] leading-10 text-ds-primary mb-6">{v2.finalCta.title}</h2>
          <p className="text-ds-on-surface-variant text-[18px] leading-7 mb-12">{v2.finalCta.intro}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href={`tel:${TEL}`}
              className="bg-ds-emergency-red text-ds-clinical-white px-10 py-5 rounded-2xl font-bold text-[20px] shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-all"
            >
              {v2.finalCta.button}
            </a>
          </div>
          <p className="mt-6 text-ds-on-surface-variant text-[14px]">
            <a href={`tel:${TEL}`} className="underline hover:text-ds-primary">{TEL_DISPLAY}</a>
          </p>
        </div>
      </section>
    </main>
  );
};

export default EmergencyPage;
